import React, { useEffect, useState } from 'react';
import {
    Typography,
    Stack,
    DialogContent,
    Button,
    Alert,
    CircularProgress,
    Box,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { container } from '@infrastructure/container';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { APP_CONFIG } from '../../../../config/app.config';

interface ShopifyConnectFormProps {
    onClose: () => void;
    connectionId?: string;
}

const colors = APP_CONFIG.ui.theme.colors;

const INSTALL_STEPS = [
    {
        title: 'Open Shopify admin',
        description: 'Use this same browser so we can link the store to your company.',
    },
    {
        title: 'Open the App Store',
        description: 'From Apps, open the Shopify App Store.',
    },
    {
        title: 'Install JustStock',
        description: 'Search for JustStock and click Install.',
    },
    {
        title: 'Approve access',
        description: 'You’ll return here to finish matching your products.',
    },
];

const ShopifyConnectForm: React.FC<ShopifyConnectFormProps> = ({ onClose, connectionId }) => {
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isPreparing, setIsPreparing] = useState(!connectionId);
    const [isReconnecting, setIsReconnecting] = useState(!!connectionId);

    useEffect(() => {
        if (connectionId) {
            reconnectWithShopify();
            return;
        }

        prepareInstallIntent();
    }, [connectionId]);

    const prepareInstallIntent = async () => {
        setValidationError(null);
        setIsPreparing(true);

        try {
            const redirectUri = `${window.location.origin}${window.location.pathname}${window.location.search || ''}`;
            const response = await container.shopifyConnectorRepository.initiateInstall(redirectUri);

            if (response.status !== 'success') {
                throw new Error(response.error?.message || 'Failed to prepare Shopify install');
            }
        } catch (error) {
            setValidationError(error instanceof Error ? error.message : 'Failed to prepare Shopify install');
        } finally {
            setIsPreparing(false);
        }
    };

    const reconnectWithShopify = async () => {
        if (!connectionId) return;

        setValidationError(null);
        setIsReconnecting(true);

        try {
            const redirectUri = `${window.location.origin}${window.location.pathname}${window.location.search || ''}`;
            const response = await container.shopifyConnectorRepository.reconnect(connectionId, redirectUri);

            if (response.status !== 'success') {
                throw new Error(response.error?.message || 'Failed to start Shopify reconnect');
            }

            window.location.href = response.data.authUrl;
        } catch (error) {
            setValidationError(error instanceof Error ? error.message : 'Failed to reconnect Shopify');
            setIsReconnecting(false);
        }
    };

    if (connectionId) {
        return (
            <>
                <DialogHeader>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight="medium">
                            Reconnecting to Shopify…
                        </Typography>
                        <IconButton onClick={onClose} color="inherit" aria-label="Close">
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogHeader>
                <DialogContent sx={{ py: 4 }}>
                    {isReconnecting ? (
                        <Stack alignItems="center" spacing={2}>
                            <CircularProgress size={28} />
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                Redirecting you to Shopify to re-authorize…
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack gap={2} alignItems="flex-start">
                            <Typography variant="body2" color="text.secondary">
                                Are you not redirected automatically?
                            </Typography>
                            <Button variant="contained" onClick={reconnectWithShopify}>
                                Open Shopify
                            </Button>
                        </Stack>
                    )}

                    {validationError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {validationError}
                        </Alert>
                    )}
                </DialogContent>
            </>
        );
    }

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight="medium">
                            Connect Shopify
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} color="inherit" aria-label="Close" disabled={isPreparing}>
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {isPreparing ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 5 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            Preparing your connection…
                        </Typography>
                    </Stack>
                ) : (
                    <Stack gap={2.5}>

                        <Typography variant="body1" color="text.secondary">
                            You can install JustStock from the Shopify App Store
                        </Typography>


                        <Stack gap={1.5}>
                            {INSTALL_STEPS.map((step, index) => (
                                <Stack
                                    key={step.title}
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        border: `1px solid ${colors.border}`,
                                        bgcolor: colors.background,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            flexShrink: 0,
                                            borderRadius: '50%',
                                            bgcolor: colors.primaryTint,
                                            color: colors.primary,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            typography: 'body2',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                    <Box sx={{ pt: 0.25 }}>
                                        <Typography variant="body2" fontWeight={600} color="text.primary">
                                            {step.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>

                    </Stack>
                )}
            </DialogContent>
        </>
    );
};

export default ShopifyConnectForm;
